import { addTimeDelta } from '@/utils/dates.js'

function IndicatorData(dataset, options) {
  Array.apply(this)

  this.dataset = dataset.slice(1)
  this.headers = dataset[0]
  Array.prototype.unshift.call(this, options.fields)
  this.headers = this.headers.reduce((acum, header, i) => {
    acum[header] = i
    return acum
  }, {})
  this.fields = options.fields.reduce((acum, fieldName, i) => {
    acum[fieldName] = i
    return acum
  }, {})
  this.power = options.power || 1
  this.timeScale = options.timeScale?.id || 'A' // 'P1Y'

  this.dataset.countries = {}
  this.countries = {}
  this.dataset.dates = [Infinity, -Infinity]
  this.dates = [Infinity, -Infinity]
  this.range = [Infinity, -Infinity]
  this.lastAvailables = {}
  this.lastAvailablesRange = [Infinity, -Infinity]
  this._columns = function(row) {
    return row
  }

  Object.defineProperty(this, 'lastAvailableData', {
    get() {
      const data = this.inherit(
        [this[0]].concat(Object.entries(this.lastAvailables).map(d => this._columns(d[1])))
      )
      data.countries = Object.fromEntries(
        Object.keys(this.lastAvailables).map(k => [k, this.countries[k]])
      )
      data.dates = [this.dates[this.dates.length - 1]]
      data.range = data.reduce(
        (range, row) => {
          return this.updateRange(range, row[this.fields._VALUE_] || row[this.fields._COLOR_])
        },
        [Infinity, -Infinity]
      )
      if (this.lollipopInfo) {
        data.lollipopInfo = this.lollipopInfo
        data.lollipopData = Object.keys(this.lollipopData).reduce((data, opt) => {
          data[opt] = [this[0]].concat(
            Object.entries(this.lastAvailableLollipops[opt]).map(d => this._columns(d[1]))
          )
          return data
        }, {})

        for (let opt in data.lollipopData) {
          const newOptValues = []
          data.forEach(row => {
            var optValue = data.lollipopData[opt].find(
              d => d[data.fields._COUNTRY_] === row[data.fields._COUNTRY_]
            )
            if (!optValue) {
              optValue = row.map((d, i) => (i !== data.fields._VALUE_ ? d : null))
            }
            newOptValues.push(optValue)
            data.range = this.updateRange(
              data.range,
              optValue[this.fields._VALUE_] || optValue[this.fields._COLOR_]
            )
          })
          data.lollipopData[opt] = newOptValues
        }
      }

      return data
    },
  })

  if (options.lollipopInfo) {
    this.lollipopInfo = options.lollipopInfo
    this.lollipopData = Object.fromEntries(
      options.lollipopInfo.options.map(opt => [opt.id, [this[0]]])
    )
    this.lastAvailableLollipops = Object.fromEntries(
      options.lollipopInfo.options.map(opt => [opt.id, {}])
    )
  }
}

IndicatorData.prototype = Object.create(Array.prototype)

IndicatorData.prototype.inherit = function(dataset) {
  for (let key in this) {
    if (typeof this[key] === 'function') {
      dataset[key] = this[key]
    }
  }
  dataset.fields = this.fields
  return dataset
}

IndicatorData.prototype.columns = function(fn) {
  this._columns = fn
  return this
}

IndicatorData.prototype.select = function(fn) {
  for (let _row of this.dataset) {
    let row = [..._row]
    const rowDate = row[this.headers._DATE_].getTime()
    row.brkDims = _row.brkDims
    row.attributes = _row.attributes
    row[this.headers._VALUE_] = row[this.headers._VALUE_] * this.power
    this.dataset.dates = this.updateRange(this.dataset.dates, rowDate)
    const country = row[this.headers._ISO3_].replace(/--.+/, '')
    this.dataset.countries[country] = true

    const rowLollipopOption = this.lollipopInfo ? row.brkDims[this.lollipopInfo.dimId] : null
    if (rowLollipopOption === null || rowLollipopOption === this.lollipopInfo.mainOption?.id) {
      // Case when it's a main breakdown value
      if (this.lastAvailables[country]) {
        if (this.lastAvailables[country][this.headers._DATE_].getTime() < rowDate) {
          this.lastAvailables[country] = row
        }
      } else {
        this.lastAvailables[country] = row
      }

      const isSelected = fn(row)
      if (isSelected) {
        this.push(this._columns(row))
        this.dates = this.updateRange(this.dates, rowDate)
        this.range = this.updateRange(this.range, row[this.headers._VALUE_])
      }

      if (isSelected !== false) {
        this.countries[row[this.headers._ISO3_]] = isSelected // Boolean(isSelected)
      }
    } else {
      // Casw when it's a lollipop breakdown value
      const rowLollipopValue = row.brkDims[this.lollipopInfo.dimId]
      if (this.lastAvailableLollipops[rowLollipopValue][country]) {
        if (
          this.lastAvailableLollipops[rowLollipopValue][country][this.headers._DATE_].getTime() <
          rowDate
        ) {
          this.lastAvailableLollipops[rowLollipopValue][country] = row
        }
      } else {
        this.lastAvailableLollipops[rowLollipopValue][country] = row
      }

      const isSelected = fn(row)
      if (isSelected) {
        this.lollipopData[rowLollipopValue].push(this._columns(row))
        this.dates = this.updateRange(this.dates, row[this.headers._DATE_].getTime())
        this.range = this.updateRange(this.range, row[this.headers._VALUE_])
      }

      if (isSelected !== false) {
        this.countries[row[this.headers._ISO3_]] = isSelected // Boolean(isSelected)
      }
    }
  }

  if (this.lollipopInfo) {
    for (let opt in this.lollipopData) {
      const newOptValues = []
      this.forEach(row => {
        var optValue = this.lollipopData[opt].find(
          d => d[this.fields._COUNTRY_] === row[this.fields._COUNTRY_]
        )
        if (!optValue) {
          optValue = row.map((d, i) => (i !== this.fields._VALUE_ ? d : null))
        }
        newOptValues.push(optValue)
        this.range = this.updateRange(this.range, optValue[this.headers._VALUE_])
      })
      this.lollipopData[opt] = newOptValues
    }
  }

  return this
}

IndicatorData.prototype.sort = function(fn) {
  Array.prototype.sort.call(this, fn)

  if (this.lollipopInfo) {
    for (let opt in this.lollipopData) {
      const sortedValues = []
      this.forEach(row => {
        const value = this.lollipopData[opt].find(
          d => d[this.fields._COUNTRY_] === row[this.fields._COUNTRY_]
        )
        sortedValues.push(value)
      })
      this.lollipopData[opt] = sortedValues
    }
  }

  return this
}

IndicatorData.prototype.updateRange = function(range, val) {
  if (!val || isNaN(val) || !isFinite(val)) return range
  return [Math.min(range[0], val), Math.max(range[1], val)]
}

IndicatorData.prototype.getExtensionGrid = function(broadbandFilter) {
  const edges = {}
  const labels = {}

  let grid = this.slice(1).reduce((ob, row) => {
    const date = row[this.fields._DATE_].getTime()
    if (!ob[date]) ob[date] = []
    edges[row[this.fields._COUNTRY_]] = edges[row[this.fields._COUNTRY_]] || [Infinity, -Infinity]
    edges[row[this.fields._COUNTRY_]] = [
      Math.min(edges[row[this.fields._COUNTRY_]][0], date),
      Math.max(edges[row[this.fields._COUNTRY_]][1], date),
    ]
    ob[date].push(row)
    labels[row[this.fields._LABEL_]] = true
    return ob
  }, {})
  const dates = Object.keys(grid)
    .map(v => +v)
    .sort((a, b) => a - b)

  let step = new Date(dates[0])
  const ceil = new Date(dates[dates.length - 1])

  do {
    if (!broadbandFilter || (broadbandFilter && step.getMonth() % 2 !== 0)) {
      grid[step.getTime()] = grid[step.getTime()] || []
    }
    step = addTimeDelta(step, this.timeScale)
  } while (step <= ceil)

  return { grid, dates, edges, countries: Object.keys(edges), labels: Object.keys(labels) }
}

IndicatorData.prototype.transposeCountries = function(broadbandFilter) {
  if (!this.length) return this

  const { grid, countries, labels } = this.getExtensionGrid(broadbandFilter)

  Object.keys(grid).map(date => {
    countries.map((iso, j) => {
      const label = labels[j]
      let isoIndex = grid[date].findIndex(row => row[this.fields._COUNTRY_] === iso)
      const countryRow = isoIndex >= 0 ? grid[date][isoIndex] : this.emptyRow(iso, date, label)
      if (isoIndex === -1) {
        grid[date].push(countryRow)
      }
    })
  })

  countries.forEach(iso => {
    const edges = [Infinity, -Infinity]
    const values = Object.keys(grid)
      .sort((a, b) => a - b)
      .reduce((values, date, i) => {
        const dateRow = grid[date].find(row => row[this.fields._COUNTRY_] === iso)
        values.push(dateRow)
        if (dateRow[this.fields._VALUE_] !== null) {
          edges[0] = Math.min(edges[0], i)
          edges[1] = Math.max(edges[1], i)
        }
        return values
      }, [])

    if (isFinite(edges[0]) && edges[1] - edges[0] !== 0) {
      let origin = values[edges[0]][this.fields._VALUE_]
      values.slice(edges[0] + 1).forEach((row, i, periode) => {
        const value = row[this.fields._VALUE_]
        if (value === null) {
          let gap = 0
          let next = null
          periode.slice(i + 1).forEach(row => {
            if (next !== null) return
            gap++
            next = row[this.fields._VALUE_]
          })
          if (next) {
            row[this.fields._VALUE_] = origin + (next - origin) / (gap + 1)
          }

          row.interpolated = true
        }
        origin = row[this.fields._VALUE_]
      })
    }
  })

  const lineRows = Object.keys(grid)
    .sort((a, b) => a - b)
    .map(date => {
      const interpolations = []
      const col = countries.map(iso => {
        const row = grid[date].find(row => row[this.fields._COUNTRY_] === iso) // [2]
        interpolations.push(row.interpolated === true)
        return row[2]
      })
      col.unshift(new Date(+date))
      col.interpolations = interpolations
      return col
    })

  let headers = [
    this[0][1],
    ...countries.map((d, i) => ({
      id: d,
      label: labels[i],
    })),
  ]
  headers.labels = labels
  const data = [headers, ...lineRows]
  data.countries = this.countries
  data.dates = this.dates
  data.range = this.range
  return data
}

IndicatorData.prototype.fullfillHeatmapGrid = function(broadbandFilter) {
  if (!this.length) return this

  let { grid, countries, labels } = this.getExtensionGrid(broadbandFilter)
  countries = Array.from(new Set(countries.map(c => c.split(/--/)[0])))

  Object.keys(grid).forEach(date => {
    countries.forEach((iso, i) => {
      if (grid[date].find(d => d[this.fields._COUNTRY_] === iso + '--' + date) === void 0) {
        const emptyRow = this.emptyRow(iso, date, labels[i])
        this.push(emptyRow)
      }
    })
  })
}

IndicatorData.prototype.emptyRow = function(iso, date, label) {
  return Object.keys(this.fields)
    .sort((a, b) => {
      return this.fields[a] - this.fields[b]
    })
    .map(k => {
      switch (k) {
        case '_COUNTRY_':
          return iso
        case '_DATE_':
          return new Date(+date)
        case '_LABEL_':
          return label
        case '_X_':
          return iso.split('--')[0]
        case '_Y_':
          return new Date(+date)
        case '_VALUE_':
          return null
        default:
          return null
      }
    })
}

IndicatorData.prototype.mergeLollipopData = function() {
  for (let opt of Object.keys(this.lollipopData)) {
    this[0].push(opt)
  }
  for (let i = 1; i < this.length; i++) {
    for (let opt in this.lollipopData) {
      this[i].push(this.lollipopData[opt][i][this.fields._VALUE_])
    }
  }

  return this
}

IndicatorData.prototype.filter = function(fn) {
  Array.prototype.filter.call(this, fn)
  return this
}

export default IndicatorData
